import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import { useDispatch } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/personalDetails";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import { APPLICANT } from "app/constants/apiEndPoints";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import { PersonalFormFourData } from "app/utils/types";
import DynamicFormFour from "./dynamicForms/DynamicFormFour";
import { handleApiError } from "app/utils/handleApiError";

const PartFour: React.FC = () => {
  const dispatch = useDispatch();
  const { applicants } = useSelector(
    (state: RootState) => state.loanTypesSteps.stepsData
  );
  const applicantId = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds.applicantID
  );
  const { mutate: submitPersonalDetails, isPending } = usePersonalDetails();
  const { formFour, ...others } = useSelector(
    (state: RootState) => state.personalDetails.stepsData
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    setError,
    register,
    clearErrors,
    watch,
  } = useForm<PersonalFormFourData>({
    defaultValues: {
      visaStatus: "",
      numberOfDependents: 0,
      maritalStatus: "",
      address: {
        street: "",
        state: "",
        postCode: "",
      },
    },
  });
  const numberOfDependents = watch("numberOfDependents");
  const visaStatus = watch("visaStatus");

  const onSubmit = (data: PersonalFormFourData) => {
    const { dependentAges, ...rest } = data;
    const dependentAgesData = (data.dependentAges || [])
      .filter((age) => age !== "" && age !== undefined)
      .join(",");

    const body = {
      ...rest,
      dependentAges:
        Number(data.numberOfDependents) === 0 ? "0" : dependentAgesData,
      numberOfDependents: Number(data.numberOfDependents),
    };
    const savedData = {
      dependentAges,
      ...rest,
    };
    submitPersonalDetails(
      {
        url: `${APPLICANT}/${applicantId}/fourth`,
        data: body,
      },
      {
        onSuccess: () => {
          dispatch(nextStep());
          dispatch(
            addStepsData({
              ...others,
              formFour: savedData,
            })
          );
        },
        onError: (error: any) => {
          handleApiError(error);
        },
      }
    );
  };
  useEffect(() => {
    if (formFour) {
      reset(formFour);
    } else {
      const { maritalStatus, numberOfDependents, address } = applicants || {};
      setValue("maritalStatus", maritalStatus || "");
      if (numberOfDependents) {
        setValue("numberOfDependents", numberOfDependents);
      }
      if (address?.postCode) {
        setValue("address.postCode", address?.postCode);
      }
      if (address?.street) {
        setValue("address.street", address?.street);
      }
      if (address?.state) {
        setValue("address.state", address?.state);
      }
    }
  }, [formFour, reset]);

  useEffect(() => {
    if (
      formFour?.dependentAges &&
      numberOfDependents !== formFour.dependentAges.length
    ) {
      setValue("dependentAges", Array(numberOfDependents).fill(""));
    }
  }, [numberOfDependents, formFour, setValue]);

  useEffect(() => {
    if (numberOfDependents === 0) {
      clearErrors("dependentAges");
    }
  }, [numberOfDependents]);

  return (
    <>
      <ProgressBar progress={75} MB={7} />
      <div className="w-full  sm:w-[80%] md:w-[80%] xl:w-[45%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />

        <form onSubmit={handleSubmit(onSubmit)}>
          <DynamicFormFour
            control={control}
            errors={errors}
            register={register}
            setValue={setValue}
            setError={setError}
            clearErrors={clearErrors}
            visaStatus="visaStatus"
            maritalStatus="maritalStatus"
            visaStatusValue={visaStatus}
            visaNumber="visaNumber"
            numberOfDependents={numberOfDependents}
            numberOfDependentsName="numberOfDependents"
            dependentAges="dependentAges"
            addressStreetName="address.street"
            addressStateName="address.state"
            addressPostCodeName="address.postCode"
          />

          <div className="flex justify-between my-10">
            <PreviousButtom onClick={() => dispatch(prevStep())}>
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartFour;
